<template>
  <section class="p-2 p-lg-0">
    <h2 class="title-2 text-primary-black">
      {{ t('profile.resetPassword') }}
    </h2>
    <p class="caption mb-4">{{ t('profile.enterYourPassword') }}</p>

    <!-- Password Fields Component -->
    <PasswordFields
      :form-values="formValues"
      :update-fn="updateFormValues"
      :show-errors="showErrors"
      :showPasswords="showPasswords"
      :invalid-fields="invalidFields"
      @togglePasswordVisibility="togglePasswordVisibility"
    />

    <InputFieldError :text="t('errorMessages.changePassword')" v-if="updateError"/>

    <div class="mb-4 d-flex justify-content-end">
      <!-- Submit Button -->
      <Button
        :id="'saveButton'"
        class="w-50"
        :aria-label="t('profile.savePassword')"
        @buttonClick="handlePasswordReset"
        :disabled="showErrors || isLoading"
        :loading="isLoading"
      >
        <template #text>
          {{
            isSubmitting
              ? t('profile.savingPassword')
              : t('profile.savePassword')
          }}
        </template>
      </Button>
    </div>
  </section>
</template>

<script lang="ts" setup>
/**
 * Imports and Dependencies
 */
// Vue and third-party imports
import { ref, Ref,  computed, ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';
import {  useStore } from 'vuex';

// Component imports
import PasswordFields from './PasswordFields.vue';
import Button from '@/components/UI/Button.vue';

// Types
import { FormValues, UpdateFn, ShowPasswords, ValidationRules, validatePasswordLength } from './EditPassword';

// Composables
import { useValidation } from '@/composables/useValidation';
import InputFieldError from '@/components/UI/InputFieldError.vue';
import { ModalId } from '@/config/modalIds';

/**
 * Localization and State Management
 */
const { t } = useI18n(); // Translation function
const store = useStore(); // Vuex store

// Store values


/**
 * Reactive State
 */
const formValues: Ref<FormValues> = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})


const showPasswords: Ref<ShowPasswords> = ref({
  current: false,
  new: false,
  confirm: false,
});

const isSubmitting = ref<boolean>(false);
const submitted: Ref<boolean> = ref(false)

// Validation
const validationRules: ValidationRules = {
  currentPassword: (value: string) => validatePasswordLength(value),
  newPassword: (value: string) => validatePasswordLength(value),
  confirmPassword: (value: string) => {
    const lengthCondition: boolean = validatePasswordLength(value)
    const passwordsEqual = formValues.value.newPassword === value
    return lengthCondition && passwordsEqual
  },
}
const {invalidFields} = useValidation(formValues, validationRules)

// Computed
const hasErrors: ComputedRef<boolean> = computed(() => {
  return invalidFields.value.size !== 0
})

const showErrors: ComputedRef<boolean> = computed(() => {
  return hasErrors.value && submitted.value
})

const isLoading: ComputedRef<boolean> = computed(() => {
    const isLoadingGetter = store.getters['auth/isLoading']
    return isLoadingGetter('changePassword')
  })

const updateError: ComputedRef<string | null> = computed(() => {
  const errorGetter = store.getters['auth/getError']
  return errorGetter('changePassword')
})



/**
 * Toggle password visibility for a specific field
 * @param field - The password field to toggle (current, new, or confirm)
 */
const togglePasswordVisibility = (field: keyof ShowPasswords): void => {
  showPasswords.value[field] = !showPasswords.value[field];
};

// Update function to pass to subforms
const updateFormValues: UpdateFn = (key, value) => {
    formValues.value[key] = value
}

/**
 * Handle password reset submission
 */
const handlePasswordReset = async (): Promise<void> => {
  submitted.value = true
  
  // Do not send the request if we have errors
  if(hasErrors.value) return

  try {
    isSubmitting.value = true;

    // Prepare payload and headers
    const payload = {
      oldPassword: formValues.value.currentPassword,
      password: formValues.value.newPassword,
    };

    // Dispatch password reset action
    await store.dispatch('auth/changePassword', { payload });
     store.commit('modal/close', ModalId.EDIT_INFO_PANEL);
  } catch (error) {
    console.error(error)
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
</style>
